import { ProductInterface } from '../../interfaces/product.interface';
import { Geo } from '../../interfaces/geo.interface';
import type { DivIconOptions } from 'leaflet';

export type LatLng = {
	lat: number;
	lng: number;
};

export type DeliveryMarkers = {
	deliveryAddress: string;
	deliveryCountryCode: string;
};

export enum ClusterIconPosition {
	top = 'top',
	bottom = 'bottom',
}

export type ClusterMarkerProps = {
	position?: ClusterIconPosition;
	verticalOffset?: number;
	width: number;
	height: number;
};

export interface SelectedLocationWithName extends Geo {
	locationName?: string;
}

export type ClusterIconPreset = ClusterMarkerProps &
	Omit<DivIconOptions, 'html'> & { html: JSX.Element };

export type ClusterIconsPresets = {
	[PinVariant.truck]: ClusterIconPreset;
};

export type PickupMarkers = {
	pickupAddress: string;
	pickupCountryCode: string;
};

export type AddressMarkers = {
	address?: string | undefined;
	countryCode?: string | undefined;
	name?: string | undefined;
	productCount?: string;
};

export type MapPolyline = {
	id: number;
	distance: number;
	trafficTime: number;
	coordinates: LatLng[];
	orderRoute: boolean;
};

export type MapMarker = {
	deliveryAddress?: string;
	pickupAddress?: string;
	address?: any;
	label?: any;
	countryCode?: string | undefined;
	lat?: number | undefined;
	lng?: number | undefined;
	latitude: number;
	longitude: number;
	variant: string;
	truck?: FleetDto;
	delivery?: DeliveryMarkers;
	pickup?: PickupMarkers;
	fleetBase?: AddressMarkers;
	radius?: {
		value: number | undefined;
		isAdjustable: boolean;
	};
	orderProduct?: ProductInterface;
	store?: AddressMarkers;
};

export interface FleetDto {
	id: number;
	name: string;
	type: string;
	truckModel: string;
	truckYear: number;
	truckColor: string;
	truckNumber: string;
	truckPhoto?: string | undefined;
	driverFullName?: string | undefined;
	driverAvatar?: string | undefined;
	createdAt: string;
	updatedAt: string;
	companyId: number;
	code: string;
	driverEmail?: string | undefined;
	driverPhone?: string | undefined;
	licensePlates?: string | undefined;
	emissionType?: string | undefined;
	truckBrand: string;
	uid: string;
	status: string;
	lastAddress?: string;
	lastLocation?: {
		type: string;
		coordinates: number[];
	};
}

export interface MapProps {
	orderStatus?: string;
	polygons?: MapPolyline[];
	markers?: MapMarker[];
	markerHovered?: string;
	currentMarker?: MapMarker;
	target?: string;
	setBaseRadius?: (radius: number) => void;
	isMapFullScreen?: boolean;
	setIsMapFullScreen?: (isMapFullScreen: boolean) => void;
	selectedLocation?: Geo;
	onConfirmLocation?: (location: Geo) => void;
	onReversePin?: (marker: ReverseGeoMarker) => void;
	mapContainerKey?: string;
	fitMarkersToBounds?: boolean;
}

export type VariantMap = { [key in PinVariant]?: JSX.Element };

export interface MapView {
	coords: LatLng;
}

export interface PinContentProps {
	countryCode?: string;
	address?: string;
	orderProduct?: ProductInterface;
	productStore?: AddressMarkers;
	companyName?: string;
	markerHovered?: string;
}

export interface MarkerContentProps {
	marker: MapMarker;
	delivery: DeliveryMarkers | undefined;
	pickup: PickupMarkers | undefined;
	fleetBase: AddressMarkers | undefined;
	orderProduct: ProductInterface | undefined;
	productStore: AddressMarkers | undefined;
	markerHovered?: string;
	target: string;
}

export interface MarkerIconProps {
	fleetBase: AddressMarkers;
	orderProduct: ProductInterface;
	productStore: AddressMarkers;
	markerHovered: string;
	variant: PinVariant;
	status?: string;
}

export interface RadiusCircleProps {
	location: { latitude: number; longitude: number };
	radius?: number;
}

export interface GroupedMarkers {
	[key: string]: MapMarker[];
}

export interface ZoomLevel {
	[key: number]: number;
}

export interface ReverseGeoMarker {
	id: string;
	position: LatLng;
	locationName?: string;
	address?: string;
}

export enum PinVariant {
	truck = 'truck',
	pickup = 'pickup',
	delivery = 'delivery',
	product = 'product',
	fleet_base = 'fleet_base',
	product_store = 'product_store',
}

export enum RouteColorVariant {
	orderRoute = '#20AC93',
	toPickup = '#FFB352',
	completed = '#494C83',
	customer = '#373A77',
}

export const StatusTruckPinColors: {
	[key: string]: string;
} = {
	available: '#20AC93',
	not_available: '#FFB352',
};
